import { useEffect, useState, type FormEvent } from "react";
import { MessageSquare, Pencil, Plus, Trash2 } from "lucide-react";
import { ApiError } from "@/lib/api";
import { Badge, Button, Dialog, Field, Input, Select } from "@/components/ui/primitives";
import { ConfirmButton } from "@/components/ui/ConfirmButton";
import {
  useCreateTemplate,
  useDeleteTemplate,
  useTemplates,
  useUpdateTemplate,
} from "./hooks";
import type { WaTemplate } from "./api";

const CATEGORIES = ["utility", "marketing", "authentication"];
const STATUSES = ["approved", "pending", "rejected"];

export default function WhatsappTemplatesPage() {
  const { data: templates } = useTemplates();
  const remove = useDeleteTemplate();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<WaTemplate | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong.");
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <MessageSquare className="h-6 w-6 text-primary" aria-hidden="true" />
            WhatsApp templates
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Pre-approved message templates for sending outside the 24-hour window.
          </p>
        </div>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          New template
        </Button>
      </div>

      {error && (
        <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-4 py-2.5 font-medium">Name</th>
              <th className="px-4 py-2.5 font-medium">Category</th>
              <th className="px-4 py-2.5 font-medium">Body</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
              <th className="px-4 py-2.5 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(templates ?? []).length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  No templates yet.
                </td>
              </tr>
            )}
            {(templates ?? []).map((t) => (
              <tr key={t.id} className="border-t border-border">
                <td className="px-4 py-3">
                  <div className="font-medium">{t.name}</div>
                  <div className="text-xs text-muted-foreground">{t.language}</div>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{t.category}</td>
                <td className="px-4 py-3 text-muted-foreground">
                  <span className="line-clamp-1 max-w-xs">{t.body}</span>
                </td>
                <td className="px-4 py-3">
                  <Badge tone={t.status === "approved" ? "success" : t.status === "rejected" ? "danger" : "warning"}>
                    {t.status}
                  </Badge>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" aria-label={`Edit ${t.name}`} onClick={() => { setEditing(t); setFormOpen(true); }}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <ConfirmButton aria-label={`Delete ${t.name}`} title="Delete template?" message={<>Delete template <span className="font-medium text-foreground">{t.name}</span>?</>} onConfirm={() => run(() => remove.mutateAsync(t.id))}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </ConfirmButton>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <TemplateDialog open={formOpen} onClose={() => setFormOpen(false)} editing={editing} />
    </div>
  );
}

function TemplateDialog({
  open,
  onClose,
  editing,
}: {
  open: boolean;
  onClose: () => void;
  editing: WaTemplate | null;
}) {
  const create = useCreateTemplate();
  const update = useUpdateTemplate();
  const [form, setForm] = useState({ name: "", language: "en", category: "utility", body: "", variables: "", status: "approved" });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm(
      editing
        ? {
            name: editing.name,
            language: editing.language,
            category: editing.category,
            body: editing.body,
            variables: (editing.variables ?? []).join(", "),
            status: editing.status,
          }
        : { name: "", language: "en", category: "utility", body: "", variables: "", status: "approved" },
    );
    setError(null);
  }, [open, editing]);

  const set = (k: keyof typeof form) => (v: string) => setForm((f) => ({ ...f, [k]: v }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const variables = form.variables.split(",").map((s) => s.trim()).filter(Boolean);
    try {
      if (editing) {
        await update.mutateAsync({
          id: editing.id,
          body: { category: form.category, body: form.body, variables, status: form.status },
        });
      } else {
        await create.mutateAsync({
          name: form.name.trim(),
          language: form.language,
          category: form.category,
          body: form.body,
          variables,
          status: form.status,
        });
      }
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save the template.");
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title={editing ? "Edit template" : "New template"}>
      <form onSubmit={submit} className="space-y-3">
        {error && (
          <div role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name" htmlFor="t_name">
            <Input id="t_name" value={form.name} disabled={editing !== null} onChange={(e) => set("name")(e.target.value)} />
          </Field>
          <Field label="Language" htmlFor="t_lang">
            <Input id="t_lang" value={form.language} disabled={editing !== null} onChange={(e) => set("language")(e.target.value)} />
          </Field>
        </div>
        <Field label="Body (use {{1}}, {{2}} for variables)" htmlFor="t_body">
          <textarea
            id="t_body"
            rows={4}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={form.body}
            onChange={(e) => set("body")(e.target.value)}
          />
        </Field>
        <Field label="Variables (comma-separated names, in order)" htmlFor="t_vars">
          <Input id="t_vars" value={form.variables} placeholder="customer_name, ticket_number" onChange={(e) => set("variables")(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Category" htmlFor="t_cat">
            <Select id="t_cat" value={form.category} onChange={(e) => set("category")(e.target.value)}>
              {CATEGORIES.map((c) => (<option key={c} value={c}>{c}</option>))}
            </Select>
          </Field>
          <Field label="Status" htmlFor="t_status">
            <Select id="t_status" value={form.status} onChange={(e) => set("status")(e.target.value)}>
              {STATUSES.map((s) => (<option key={s} value={s}>{s}</option>))}
            </Select>
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={!form.name.trim() || !form.body.trim() || create.isPending || update.isPending}>
            {editing ? "Save changes" : "Create template"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
